import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Alert, Button, Form, Spinner, Table } from "react-bootstrap";
import api from "../api";

// Period options offered to the director. Values match the backend
// /dashboard/admin/services-report `period` param.
const PERIOD_OPTIONS = [
  { key: "month",     label: "This Month" },
  { key: "quarter",   label: "This Quarter" },
  { key: "year",      label: "This Year" },
  { key: "last_year", label: "Last Year" },
];

function monthKey(row) {
  return `${row.year}-${String(row.month).padStart(2, "0")}`;
}

export default function ServicesReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const period = PERIOD_OPTIONS.some((p) => p.key === searchParams.get("period"))
    ? searchParams.get("period")
    : "month";

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api
      .get("/dashboard/admin/services-report", { params: { period } })
      .then((res) => setData(res.data))
      .catch(() => setError("Failed to load the services report."))
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const changePeriod = (next) => {
    setSearchParams({ period: next });
  };

  // Group the flat rows into month sections so multi-month windows print cleanly.
  const groups = [];
  if (data?.items?.length) {
    const byMonth = new Map();
    for (const row of data.items) {
      const k = monthKey(row);
      if (!byMonth.has(k)) {
        byMonth.set(k, { key: k, title: `${row.month_name} ${row.year}`, rows: [] });
      }
      byMonth.get(k).rows.push(row);
    }
    groups.push(...byMonth.values());
  }

  const generatedStr = data?.generated_at
    ? new Date(data.generated_at).toLocaleString("en-US", {
        timeZone: "America/Detroit",
        month: "long", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
      })
    : "";

  return (
    <div className="services-report">
      {/* ── Controls (hidden when printing) ─────────────────────────────── */}
      <div className="no-print d-flex flex-wrap justify-content-between align-items-end gap-2 mb-4">
        <div>
          <Button variant="link" className="p-0 mb-1 text-decoration-none" onClick={() => navigate("/admin-dashboard")}>
            ← Back to Dashboard
          </Button>
          <h4 className="mb-0">Services Summary Report</h4>
        </div>
        <div className="d-flex align-items-end gap-2">
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Reporting period</Form.Label>
            <Form.Select
              value={period}
              onChange={(e) => changePeriod(e.target.value)}
              style={{ minWidth: 180 }}
            >
              {PERIOD_OPTIONS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Button variant="primary" onClick={() => window.print()} disabled={loading || !!error}>
            <i className="bi bi-printer me-1" /> Print
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" className="no-print">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : data && !error && (
        <>
          {/* ── Printable header ──────────────────────────────────────────── */}
          <div className="report-header text-center mb-4">
            <h3 className="mb-1">His Helping Hands of Clare County</h3>
            <h5 className="text-muted mb-2">Services Summary Report</h5>
            <div className="fw-semibold">{data.period_label}</div>
            {generatedStr && (
              <div className="text-muted small">Generated {generatedStr}</div>
            )}
          </div>

          {groups.length === 0 ? (
            <p className="text-muted text-center py-4">
              No services recorded for this period.
            </p>
          ) : (
            groups.map((g) => (
              <div key={g.key} className="report-section mb-4">
                <h6 className="text-uppercase fw-bold border-bottom pb-1 mb-2">{g.title}</h6>
                <Table bordered size="sm" className="report-table mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Category</th>
                      <th>Bed Type</th>
                      <th className="text-end" style={{ width: "8rem" }}>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((row, i) => (
                      <tr key={i}>
                        <td>{row.category}</td>
                        <td>{row.bed_type || "—"}</td>
                        <td className="text-end">{row.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}
